// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  getUniqueViolationColumnId,
  handleAPIError,
  hasSkuExistsError,
  hasUniqueViolationError,
} from '@/api/error-utils';
import { ExistingItem } from '@/api/types';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  AutoAwesomeOutlined,
  CloseRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  DEFAULT_UNIT_OF_MEASURE,
  unitOfMeasureSchema,
} from '@/constants/unit-of-measure';
import { useToast } from '@/contexts/toast-context';
import { CreateItemParams, createItem } from '@/items/api/create-item';
import { ItemTypePicker } from '@/items/components/item-type-picker';
import { TakenSkuLink } from '@/items/components/taken-sku-link';
import { UnitOfMeasureSelect } from '@/items/components/unit-of-measure-select';
import {
  ITEM_TYPE_DESCRIPTIONS,
  ITEM_TYPE_ICONS,
  ITEM_TYPE_LABELS,
  type ItemType,
} from '@/items/constants/item-types';
import { itemsQueryKey } from '@/items/hooks/use-items';
import { generateSkuSuggestion } from '@/items/utilities/generate-sku-suggestion';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Link as MuiLink,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Unified form schema that includes all fields.
 * BULK-specific fields have default values and are only used when type is BULK.
 */
const formSchema = z.object({
  sku: z.string().min(1, 'SKU is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable(),
  unitOfMeasure: unitOfMeasureSchema,
  acceptsBulkSerials: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

function getDefaultValues(initialName?: string): FormData {
  return {
    sku: initialName ?? '',
    name: initialName ?? '',
    description: null,
    unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
    acceptsBulkSerials: false,
  };
}

interface Props {
  /**
   * Controls the visibility of the dialog.
   */
  isOpen: boolean;
  /**
   * Callback fired when the dialog should close.
   */
  onClose: () => void;
  /**
   * Optional initial name used to pre-populate the name field.
   */
  initialName?: string;
  /**
   * Optional initial type used to skip the type selection step.
   */
  initialType?: ItemType;
}

/**
 * Dialog component for creating new items in the item catalog.
 *
 * Features:
 * - Type selection using ItemTypePicker (Service, Device, or Bulk)
 * - Common fields: SKU, Name, Description
 * - AI-powered SKU suggestion for Service and Device types
 * - Bulk-specific fields: Unit of measure, Bulk item accepts serial numbers
 * - Form validation using Zod
 * - Error handling for duplicate name/SKU
 * - Success toast with link to created item
 */
/**
 * Returns the display label for an item type, with "item" appended for BULK.
 * Used in dialog titles and error messages.
 */
function getTypeDisplayLabel(type: ItemType | null): string {
  if (!type) {
    return 'item';
  }
  if (type === 'BULK') {
    return 'part';
  }
  return ITEM_TYPE_LABELS[type].toLowerCase();
}

export function CreateItemDialog(props: Props) {
  const { isOpen, onClose, initialName, initialType } = props;
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [step, setStep] = useState<'select' | 'form'>('select');
  const [selectedType, setSelectedType] = useState<ItemType | null>(null);
  const [takenSkuItem, setTakenSkuItem] = useState<ExistingItem | null>(null);

  const typeDisplayLabel = getTypeDisplayLabel(selectedType);

  const { control, handleSubmit, reset, setError, watch, setValue, formState } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: getDefaultValues(initialName),
    });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset(getDefaultValues(initialName));

    if (initialType) {
      setSelectedType(initialType);
      setStep('form');
      return;
    }

    setSelectedType(null);
    setStep('select');
  }, [initialName, initialType, isOpen, reset]);

  // Watch fields for SKU suggestion
  const currentName = watch('name');
  const currentSku = watch('sku');

  // Generate SKU suggestion from item name
  const skuSuggestion = useMemo(() => {
    if (!currentName || currentName.trim() === '') {
      return null;
    }
    return generateSkuSuggestion(currentName);
  }, [currentName]);

  // Show suggestion when name has content, suggestion exists, and SKU is empty
  const showSkuSuggestion =
    skuSuggestion !== null && (currentSku === null || currentSku === '');

  const handleAcceptSkuSuggestion = (e: React.MouseEvent) => {
    e.preventDefault();
    if (skuSuggestion) {
      setValue('sku', skuSuggestion.sku, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const createItemMutation = useMutation({
    mutationFn: createItem,
    onSuccess: (result) => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: itemsQueryKey });
      queryClient.invalidateQueries({ queryKey: ['item'] });

      // Show success toast with link
      const typeLabel = ITEM_TYPE_LABELS[result.itemType];
      const linkTo = `/catalog/items/${result.key}`;

      showToast({
        severity: 'success',
        duration: 5000,
        element: (
          <span>
            {typeLabel}{' '}
            <RouterLink to={linkTo}>
              <MuiLink
                component="span"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                {result.sku}
              </MuiLink>
            </RouterLink>{' '}
            created
          </span>
        ),
      });

      // Close dialog directly - don't use handleClose() which has isPending guard
      onClose();
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        const columnId = getUniqueViolationColumnId(error);
        if (columnId === 'name') {
          setError('name', {
            type: 'custom',
            message: `A ${typeDisplayLabel} with this name already exists`,
          });
        } else {
          setError('sku', {
            type: 'custom',
            message: `A ${typeDisplayLabel} with this SKU already exists`,
          });
        }
      } else if (hasSkuExistsError(error)) {
        setTakenSkuItem(handleAPIError(error).existingItem ?? null);
        setError('sku', {
          type: 'custom',
          message: `A ${typeDisplayLabel} with this SKU already exists`,
        });
      }
    },
  });

  const isAlertOpen =
    createItemMutation.isError &&
    !hasUniqueViolationError(createItemMutation.error) &&
    !hasSkuExistsError(createItemMutation.error) &&
    !isPlanLimitError(createItemMutation.error);

  const handleClose = () => {
    if (createItemMutation.isPending) {
      return;
    }
    onClose();
  };

  const handleExited = () => {
    setStep('select');
    setSelectedType(null);
    setTakenSkuItem(null);
    reset(getDefaultValues());
    createItemMutation.reset();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (createItemMutation.isPending) {
        return;
      }
    }
    handleClose();
  };

  const handleTypeSelect = (type: ItemType) => {
    setSelectedType(type);
    setStep('form');
  };

  const handleTypeToggle = (
    _event: React.MouseEvent<HTMLElement>,
    newType: ItemType | null
  ) => {
    if (newType !== null) {
      setSelectedType(newType);
    }
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (!selectedType) {
      return;
    }

    let params: CreateItemParams;

    if (selectedType === 'BULK') {
      params = {
        type: 'BULK',
        sku: data.sku,
        name: data.name,
        description: data.description,
        unitOfMeasure: data.unitOfMeasure,
        acceptsBulkSerials: data.acceptsBulkSerials,
      };
    } else {
      params = {
        type: selectedType,
        sku: data.sku,
        name: data.name,
        description: data.description,
      };
    }

    setTakenSkuItem(null);
    createItemMutation.mutate(params);
  };

  // Read every render, so react-hook-form subscribes to the SKU error.
  const hasSkuError = formState.errors.sku !== undefined;

  const isSubmitDisabled =
    step !== 'form' ||
    !selectedType ||
    createItemMutation.isPending ||
    !formState.isValid;

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onExited: handleExited,
      }}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while creating the item."
        onClose={createItemMutation.reset}
      />

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            {step === 'select' ? 'Add item' : `Add ${typeDisplayLabel}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {step === 'select'
              ? 'Select the type of item to add'
              : `Add a new ${typeDisplayLabel} to the catalog`}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={createItemMutation.isPending}
          aria-label="Close"
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {/* Step 1: Type selection */}
          {step === 'select' && (
            <ItemTypePicker
              selectedType={selectedType}
              onSelect={handleTypeSelect}
            />
          )}

          {/* Step 2: Form with type toggle */}
          {step === 'form' && selectedType && (
            <>
              {/* Type toggle bar */}
              <ToggleButtonGroup
                value={selectedType}
                exclusive
                onChange={handleTypeToggle}
                fullWidth
                size="small"
              >
                {(['SERVICE', 'DEVICE', 'BULK'] as const).map((type) => {
                  const Icon = ITEM_TYPE_ICONS[type];
                  return (
                    <Tooltip
                      key={type}
                      title={ITEM_TYPE_DESCRIPTIONS[type]}
                      arrow
                    >
                      <ToggleButton
                        value={type}
                        sx={{
                          textTransform: 'none',
                          gap: 0.5,
                        }}
                      >
                        <Icon fontSize="small" />
                        {ITEM_TYPE_LABELS[type]}
                      </ToggleButton>
                    </Tooltip>
                  );
                })}
              </ToggleButtonGroup>

              <Divider />

              <Stack spacing={2}>
                {/* Name field */}
                <FormTextField
                  name="name"
                  label="Name"
                  control={control}
                  required
                />

                {/* SKU field with suggestion */}
                <Stack spacing={0.5}>
                  <FormTextField
                    name="sku"
                    label="SKU"
                    control={control}
                    required
                  />
                  {takenSkuItem !== null && hasSkuError && (
                    <TakenSkuLink existingItem={takenSkuItem} />
                  )}
                  {showSkuSuggestion && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      px={2}
                    >
                      <AutoAwesomeOutlined
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: 16 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Suggested by Hardfin AI:
                      </Typography>
                      <Link
                        href="#"
                        onClick={handleAcceptSkuSuggestion}
                        variant="caption"
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        {skuSuggestion?.sku}
                      </Link>
                    </Stack>
                  )}
                </Stack>

                {/* Description field */}
                <FormTextField
                  name="description"
                  label="Description"
                  control={control}
                  isMultiline
                  minimumRows={2}
                />

                {/* BULK-specific fields */}
                {selectedType === 'BULK' && (
                  <>
                    <UnitOfMeasureSelect
                      name="unitOfMeasure"
                      label="Unit of measure"
                      control={control}
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2">
                          Allow users to enter serials for this item
                        </Typography>
                        <Tooltip
                          arrow
                          placement="right"
                          title="When allocating units of this part, such as during assembly, users will have the option to manually enter serial numbers for future reference (this does not make the part units into serialized assets)"
                        >
                          <InfoOutlined
                            fontSize="small"
                            color="action"
                            sx={{ fontSize: 16 }}
                          />
                        </Tooltip>
                      </Stack>
                      <Controller
                        name="acceptsBulkSerials"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            size="small"
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <Divider />
      <DialogActions>
        <Button
          variant="text"
          onClick={handleClose}
          disabled={createItemMutation.isPending}
        >
          Cancel
        </Button>
        {step === 'form' && (
          <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
            {createItemMutation.isPending ? 'Adding...' : 'Add'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
